import { PageDesignComponent } from "../../add-element/design-components/PageDesignComponent";
import { PageSlugs } from "../../add-element/types";
import { isUsingNewComponents } from "../../add-element/utils";
import { SITE_TYPES } from "../../core/constants";
import WebsiteCustomPage from "../../custom-pages/website/WebsiteCustomPage";
import Events from "../../events/website/Events";
import PageComponent from "../../pages/website/Page";
import { Photos } from "../../photos/website/Photos";
import CashRegistry from "../../registries/dashboard/CashRegistry";
import Registry from "../../registries/website/Registry";
import {
  Accommodation,
  Attendant,
  Configuration,
  Page,
  Site,
  WebsiteEvent,
} from "../../resources/modelTypes";
import RSVP from "../../rsvp/website/RSVP";
import Travel from "../../travel/website/Travel";
import WeddingParty from "../../wedding-party/website/WeddingParty";
import { AllResources } from "../selectors";
import { getCashRegistrySlug } from "../utils";
import { PageDisplayModes } from "./EditDesign/constants";
import EmptyPage from "./EmptyPage";
import { CommentSection } from "./comment/CommentSection";
import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import isEmpty from "lodash/isEmpty";
import React from "react";

const websitePagesStyles = {
  wrapper: (isWedding: boolean) => css`
    & > div {
      margin-bottom: ${isWedding ? styleUtils.rem(80) : styleUtils.rem(40)};
    }
    & > div:last-child {
      margin-bottom: ${isWedding
        ? styleUtils.rem(50)
        : styleUtils.rem(10)} !important;
    }
  `,
};

const dashboardUrl = (siteId: number) => `/site/${siteId}`;

const renderEvents = (
  { key, ref, siteId, ...props }: BaseSharedProps,
  events: WebsiteEvent[],
  source: string,
) => {
  if (isEmpty(events)) {
    return (
      <div key={key} ref={ref}>
        <EmptyPage
          {...props}
          headline="You haven't created any public events yet."
          removeSpacing
          source={source}
        >
          Once you&apos;ve created a public event in the{" "}
          <a href={`${dashboardUrl(siteId)}/pages/events`} target="_parent">
            Wedding Website Manager
          </a>
          , it will show up here with details, directions, and maps.
        </EmptyPage>
      </div>
    );
  }

  return (
    <div key={key} ref={ref}>
      <div>
        <Events {...props} events={events} removeSpacing />
      </div>
    </div>
  );
};

const renderWeddingParty = (
  { key, ref, ...props }: BaseSharedProps,
  attendants: Attendant[],
  source: string,
) => {
  if (isEmpty(attendants)) {
    return (
      <div key={key} ref={ref}>
        <EmptyPage
          {...props}
          headline="You haven't added your wedding party yet."
          removeSpacing
          source={source}
        >
          Once you add a person in your wedding party, they will show up here.
        </EmptyPage>
      </div>
    );
  }

  return (
    <div key={key} ref={ref}>
      <WeddingParty {...props} attendants={attendants} />
    </div>
  );
};

const renderTravel = (
  { key, ref, siteId, ...props }: BaseSharedProps,
  accommodations: Accommodation[],
  source: string,
) => {
  if (isEmpty(accommodations)) {
    return (
      <div key={key} ref={ref}>
        <EmptyPage
          {...props}
          headline="You haven't added any travel accommodations yet."
          removeSpacing
          source={source}
        >
          Once you&apos;ve added accommodations in the{" "}
          <a
            href={`${dashboardUrl(siteId)}/pages/accommodations`}
            target="_parent"
          >
            Wedding Website Manager
          </a>
          , it will show up here with all the details.
        </EmptyPage>
      </div>
    );
  }

  return (
    <div key={key} ref={ref}>
      <Travel {...props} accommodations={accommodations} />
    </div>
  );
};

const renderMedia = (
  { key, ref, ...props }: BaseSharedProps,
  site: Site,
  page: Page,
  source: string,
) => (
  <div key={key} ref={ref}>
    <Photos
      {...props}
      dashboardUrl={dashboardUrl}
      isMultiPage={site.pageConfiguration === PageDisplayModes.multiPage}
      page={page}
      site={site}
      source={source}
    />
  </div>
);

const renderRegistries = (
  { key, ref, siteId, siteType, ...props }: BaseSharedProps,
  registries,
  source,
) => {
  if (isEmpty(registries)) {
    const isOnlineInvitation = siteType === SITE_TYPES.ONLINE_INVITATION;
    const isWedding = siteType === SITE_TYPES.WEDDING;
    const dashboardRoute = isOnlineInvitation ? "sections" : "pages";

    return (
      <div key={key} ref={ref}>
        <EmptyPage
          {...props}
          headline="You haven't added any gift registries yet."
          removeSpacing
          source={source}
        >
          {isWedding && (
            <>
              Once you&apos;ve added a registry in the{" "}
              <a
                href={`${dashboardUrl(siteId)}/${dashboardRoute}/registry`}
                target="_parent"
              >
                Wedding Website Manager
              </a>
              , it will show up here with all the details.
            </>
          )}
          {isOnlineInvitation && (
            <>
              This section will not be visible on your invitation until you{" "}
              <a
                href={`${dashboardUrl(siteId)}/${dashboardRoute}/registry`}
                target="_parent"
              >
                add a registry
              </a>
              .
            </>
          )}
        </EmptyPage>
      </div>
    );
  }

  return (
    <div key={key} ref={ref}>
      <PageComponent
        id={props.id}
        pageData={props.pageData}
        removeSpacing
        title={props.title}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            box-sizing: border-box;
            padding-left: ${theme.spacing.x4};
            padding-right: ${theme.spacing.x4};
            gap: ${theme.spacing.x4};
          `}
        >
          {registries.map((registry) => {
            if (registry.cashRegistryId) {
              const cashRegistrySlug = getCashRegistrySlug(registry.url);

              if (cashRegistrySlug) {
                return (
                  <CashRegistry
                    key={registry.id}
                    registryId={registry.cashRegistryId}
                    registrySlug={cashRegistrySlug}
                  />
                );
              }
            } else {
              return <Registry key={registry.id} {...registry} />;
            }
          })}
        </div>
      </PageComponent>
    </div>
  );
};

const renderRsvp = (
  { key, ref, ...props }: BaseSharedProps,
  site: Site,
  source: string,
) => {
  if (!site.hasRsvpEvents) {
    return (
      <div key={key} ref={ref}>
        <EmptyPage
          {...props}
          headline="You haven't created any RSVP-enabled events yet."
          removeSpacing
          source={source}
          title={props.title}
        >
          Once you&apos;ve created an RSVP-enabled event in the{" "}
          <a href={`${dashboardUrl(site.id)}/pages/events`} target="_parent">
            Wedding Website Manager
          </a>
          , your guests will be able to submit an RSVP through your website.
        </EmptyPage>
      </div>
    );
  }

  return (
    <div key={key} ref={ref}>
      <RSVP {...props} title={props.title} />
    </div>
  );
};

const renderAddElementWebsitePage = (
  displayMode: string,
  baseProps: BaseSharedProps,
) => (
  <div key={baseProps.key} ref={baseProps.ref}>
    <PageDesignComponent displayMode={displayMode} page={baseProps.pageData} />
  </div>
);

const renderCustom = ({ key, ref, ...props }: BaseSharedProps) => (
  <div key={key} ref={ref}>
    <WebsiteCustomPage {...props} />
  </div>
);

const renderGuestBook = ({ key, ref, ...props }: BaseSharedProps) => (
  <div key={key} ref={ref}>
    <PageComponent
      id={props.id}
      pageData={props.pageData}
      removeSpacing
      title={props.title}
    >
      <CommentSection />
    </PageComponent>
  </div>
);

export type WebsitePagesProps = {
  pages: Page[];
  site: Site;
  source: string;
  resources: AllResources;
};

export type BaseSharedProps = {
  configuration: Configuration;
  id: string;
  key: string;
  pageData: Page;
  ref?: any;
  siteId: number;
  siteType: string;
  title: string;
};

const WebsitePages = ({
  pages,
  resources,
  site,
  source,
}: WebsitePagesProps) => {
  const renderedPages = pages.map((page: Page) => {
    const { configuration, givenName, isDefault, slug } = page;
    const baseProps: BaseSharedProps = {
      configuration,
      id: slug,
      key: slug,
      pageData: page,
      siteId: site.id,
      siteType: site.siteType,
      title: givenName,
    };

    // New system treats every page the same
    if (isUsingNewComponents(site)) {
      return renderAddElementWebsitePage(site.pageConfiguration, baseProps);
    }

    if (isDefault) {
      switch (slug) {
        case PageSlugs.Events:
          return renderEvents(baseProps, resources.publicEvents, source);
        case "accommodations":
          return renderTravel(baseProps, resources.accommodations, source);
        case "wedding-party":
          return renderWeddingParty(baseProps, resources.attendants, source);
        case "photos":
          return renderMedia(baseProps, site, page, source);
        case "registry":
          return renderRegistries(baseProps, resources.registries, source);
        case "rsvp":
          return renderRsvp(baseProps, site, source);
        case "guest-book":
          return renderGuestBook(baseProps);
      }
    } else {
      return renderCustom(baseProps);
    }
  });

  return (
    <div css={websitePagesStyles.wrapper(site.isWedding)}>{renderedPages}</div>
  );
};

export default WebsitePages;
